import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../../../context/AppContext';
import { enhancedGoogleDocsService } from '../../../services/enhancedGoogleDocsService';
import { debugService } from '../../../services/debugService';

const { 
  FiLink, 
  FiCheck, 
  FiAlertTriangle, 
  FiEye, 
  FiZap, 
  FiRefreshCw,
  FiGlobe,
  FiLock,
  FiUnlock
} = FiIcons;

function DirectLinkStep() {
  const { state, dispatch } = useApp();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [parseResults, setParseResults] = useState(null);
  const [urlValidation, setUrlValidation] = useState(null);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: state.wizardData.design
  });

  const watchedUrl = watch('googleDocUrl');

  // Validate URL in real-time
  React.useEffect(() => {
    if (watchedUrl) {
      validateDocumentUrl(watchedUrl);
    } else {
      setUrlValidation(null);
    }
  }, [watchedUrl]);

  const validateDocumentUrl = (url) => {
    const validation = {
      valid: false,
      isGoogleDocs: false,
      hasDocId: false,
      isPublic: false,
      errors: []
    };

    if (!url || typeof url !== 'string') {
      validation.errors.push('URL is required');
      setUrlValidation(validation);
      return validation;
    }

    // Check if it's a Google Docs URL
    if (url.includes('docs.google.com')) {
      validation.isGoogleDocs = true;
    } else {
      validation.errors.push('Must be a Google Docs URL');
    }

    // Check for document ID
    if (url.includes('/document/d/')) {
      validation.hasDocId = true;
    } else {
      validation.errors.push('Must contain a valid document ID');
    }

    // Check if it appears to be public/shareable
    const publicPatterns = [
      /\/pub\?/,
      /sharing/,
      /usp=sharing/,
      /edit\?usp=sharing/
    ];
    
    validation.isPublic = publicPatterns.some(pattern => pattern.test(url));

    validation.valid = validation.isGoogleDocs && validation.hasDocId;

    setUrlValidation(validation);
    return validation;
  };

  const onSubmit = async (data) => {
    if (!data.googleDocUrl.trim()) return;

    setIsProcessing(true);
    setProcessingStatus(null);
    setParseResults(null);

    try {
      debugService.log('info', 'googledocs', 'Starting direct link parsing', {
        url: data.googleDocUrl
      });

      // Use enhanced Google Docs service for parsing
      const results = await enhancedGoogleDocsService.parseGoogleDocTemplate(data.googleDocUrl, {
        enableDebugging: true,
        validatePlaceholders: true,
        method: 'direct-link'
      });

      setParseResults(results);

      const updatedData = {
        ...data,
        templateFields: results.placeholders,
        templateContent: results.content,
        accessMethod: 'direct-link',
        documentMetadata: results.metadata
      };

      dispatch({ type: 'UPDATE_WIZARD_DATA', step: 'design', payload: updatedData });

      setProcessingStatus('success');
      toast.success(`Template processed successfully! Found ${results.placeholders.length} placeholders`);

      // Auto-advance to next step after 2 seconds
      setTimeout(() => {
        dispatch({ type: 'SET_CURRENT_STEP', payload: 3 });
      }, 2000);

    } catch (error) {
      debugService.log('error', 'googledocs', 'Direct link parsing failed', {
        error: error.message,
        url: data.googleDocUrl
      });

      setProcessingStatus('error');
      toast.error('Failed to process template: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 md:space-y-8 px-4 md:px-0">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-3 rounded-xl">
              <SafeIcon icon={FiGlobe} className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Direct Link Template</h1>
              <p className="text-sm md:text-base text-gray-600">No API required - use Google Docs sharing link</p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* URL Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Docs Shareable URL *
            </label>
            <div className="relative">
              <input
                {...register('googleDocUrl', {
                  required: 'Google Docs URL is required',
                  pattern: {
                    value: /^https:\/\/docs\.google\.com\/document\/d\/[a-zA-Z0-9-_]+/,
                    message: 'Please enter a valid Google Docs URL'
                  }
                })}
                className="w-full px-4 py-3 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base"
                placeholder="https://docs.google.com/document/d/your-document-id/edit"
              />
              {isProcessing && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SafeIcon icon={FiRefreshCw} className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              )}
            </div>
            {errors.googleDocUrl && (
              <p className="text-red-600 text-sm mt-1">{errors.googleDocUrl.message}</p>
            )}
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              Make sure your Google Doc is publicly accessible or shared with viewing permissions
            </p>
          </div>

          {/* URL Validation Results */}
          {urlValidation && watchedUrl && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg border ${
                urlValidation.valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <h4 className={`font-medium mb-3 ${
                urlValidation.valid ? 'text-green-900' : 'text-red-900'
              }`}>
                URL Validation
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <SafeIcon 
                    icon={urlValidation.isGoogleDocs ? FiCheck : FiAlertTriangle} 
                    className={`w-4 h-4 ${urlValidation.isGoogleDocs ? 'text-green-600' : 'text-red-600'}`} 
                  />
                  <span>Google Docs URL</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon 
                    icon={urlValidation.hasDocId ? FiCheck : FiAlertTriangle} 
                    className={`w-4 h-4 ${urlValidation.hasDocId ? 'text-green-600' : 'text-red-600'}`} 
                  />
                  <span>Valid Document ID</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon 
                    icon={urlValidation.isPublic ? FiUnlock : FiLock} 
                    className={`w-4 h-4 ${urlValidation.isPublic ? 'text-green-600' : 'text-orange-600'}`} 
                  />
                  <span>{urlValidation.isPublic ? 'Public/Shared' : 'Private Access'}</span>
                </div>
              </div>

              {urlValidation.errors.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm text-red-700">
                    <strong>Issues:</strong>
                    <ul className="ml-4 mt-1 list-disc">
                      {urlValidation.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Process Button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={!watchedUrl?.trim() || isProcessing || !urlValidation?.valid}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {isProcessing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <SafeIcon icon={FiZap} className="w-5 h-5" />
              )}
              <span>{isProcessing ? 'Processing...' : 'Process Template'}</span>
            </button>

            {processingStatus && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg w-full sm:w-auto ${
                  processingStatus === 'success' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}
              >
                <SafeIcon 
                  icon={processingStatus === 'success' ? FiCheck : FiAlertTriangle} 
                  className="w-5 h-5 flex-shrink-0" 
                />
                <span className="font-medium text-sm">
                  {processingStatus === 'success' 
                    ? 'Template processed successfully!' 
                    : 'Processing failed - Please check URL'
                  }
                </span>
              </motion.div>
            )}
          </div>
        </form>
      </motion.div>

      {/* Parse Results */}
      {parseResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <SafeIcon icon={FiEye} className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-gray-900">Parse Results</h2>
              <p className="text-sm md:text-base text-gray-600">
                Found {parseResults.placeholders.length} placeholder fields
              </p>
            </div>
          </div>

          {/* Placeholder Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {parseResults.placeholders.map((field, index) => (
              <motion.div
                key={field}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-blue-50 p-3 rounded-lg border border-blue-200"
              >
                <code className="text-blue-800 font-medium text-sm break-all">{'{{'}{field}{'}}'}</code>
              </motion.div>
            ))}
          </div>

          {/* Ready Message */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">Ready for Field Mapping</h4>
            <p className="text-sm text-green-700">
              Template fields have been detected successfully. You can now proceed to map these fields to your Airtable columns.
            </p>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8"
      >
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-6">How to Share Your Google Doc</h2>
        
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Step 1: Open Share Settings</h3>
            <p className="text-sm text-blue-700">
              In your Google Doc, click the &quot;Share&quot; button in the top right corner.
            </p>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">Step 2: Set Permissions</h3>
            <p className="text-sm text-green-700">
              Change access to &quot;Anyone with the link&quot; and set permission to &quot;Viewer&quot;.
            </p>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-medium text-purple-900 mb-2">Step 3: Copy Link</h3>
            <p className="text-sm text-purple-700">
              Copy the shareable link and paste it in the URL field above.
            </p>
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <h3 className="font-medium text-orange-900 mb-2">Template Format</h3>
            <div className="text-sm text-orange-700 space-y-1">
              <p>• Use double curly braces: <code className="bg-orange-100 px-1 rounded">{'{{field_name}}'}</code></p>
              <p>• Example: <code className="bg-orange-100 px-1 rounded">{'{{customer_name}}'}</code>, <code className="bg-orange-100 px-1 rounded">{'{{invoice_date}}'}</code></p>
              <p>• Field names should match your Airtable columns</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default DirectLinkStep;